import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  ViewEncapsulation,
  inject,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSidenav } from '@angular/material/sidenav';
import { ActivatedRoute, EventType, Router } from '@angular/router';
import { L10N_LOCALE, L10nLocale, L10nTranslationService } from 'angular-l10n';
import { Subject, takeUntil } from 'rxjs';
import { SettingsApi, SupportInfo, Theme, User } from '../../hickory-api';
import { AnalyticsHelper } from '../analytics/analytics-helper';
import { FeaturesSupportedService } from '../feature-filtering/features-supported-service';
import { ApiSelectorComponent } from '../api-selector/api-selector.component';
import { ApiSelectorService } from '../api-selector/api-selector.service';
import { AppVersionService } from '../app-version.service';
import { AuthEventsComponent } from '../authentication/auth-events/auth-events.component';
import { AuthService } from '../authentication/auth.service';
import { FeedbackComponent } from '../feedback/feedback.component';
import { ImpersonationComponent } from '../impersonation/impersonation.component';
import { LogHelper } from '../logging/log-helper';
import { ConfirmComponent } from '../modals/confirm/confirm.component';
import {
  SelectFromListDialogComponent,
  SelectFromListResult,
} from '../modals/select-from-list-dialog/select-from-list-dialog.component';
import { NavigationElement, NavService } from '../navigation/nav.service';
import { ViewState } from '../responsive.service';
import { StorageHelper } from '../storage/storage-helper';
import { LayoutService } from './layout.service';
import { FilesViewConstants } from 'src/app/files/file-view-context';
import { AccessControl } from '../authentication/access-control';
import { ChangePasswordDialogComponent } from 'src/app/user/change-password-dialog/change-password-dialog.component';

@Component({
  selector: 'app-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss', '../styles/colors.scss', '../styles/globals.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class LayoutComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly locale = inject<L10nLocale>(L10N_LOCALE);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly authService = inject(AuthService);
  private readonly settingsApi = inject(SettingsApi);
  private readonly navService = inject(NavService);
  private readonly layoutService = inject(LayoutService);
  private readonly apiService = inject(ApiSelectorService);
  private readonly appVersionService = inject(AppVersionService);
  private readonly translation = inject(L10nTranslationService);
  private readonly dialog = inject(MatDialog);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  @ViewChild('navDrawer') navDrawer: MatSidenav;
  @ViewChild('changeSupportedFeatures') changeSupportedFeatures: TemplateRef<unknown>;

  currentPage: string;
  user: User;
  loadingRouteConfig: boolean = false;
  isBetaUx: boolean = false;
  isBickory: boolean = false;
  isBetaExit: boolean = false;
  isNonAdmin: boolean = false;

  theme: Theme;
  get logoUri(): string { return 'url(' + (this.theme?.logoUri ?? 'https://cdn.alertsense.com/res/images/konexus/logos/konexus-logo-mono-white-padded.png') + ')'; }
  get primaryColor(): string { return this.theme?.primaryColor ?? '#0073cf'; }
  get secondaryColor(): string { return this.theme?.secondaryColor ?? 'white'; }
  get supportInfo(): SupportInfo { return this.theme?.internalSupport; }
  get navElements(): NavigationElement[] { return this.navService.getNavigation().elements; }
  get isSuperAdmin(): boolean { return this.authService.isSuperAdmin(); }
  get canImpersonate(): boolean { return this.isSuperAdmin; }
  get isImpersonating(): boolean { return this.authService.isImpersonating(); }
  get isChangeEnvironmentsEnabled(): boolean { return this.apiService.isEnabled; }
  get isChangedEnvironment(): boolean { return this.apiService.isActive; }
  get viewState(): ViewState { return this.layoutService.responsiveViewState; }
  get toolbarHeight(): number { return this.hideFraming ? 0 : this.layoutService.layoutHeightOffsetTop; }
  get canChangePassword(): boolean { return !!this.user && !this.isSSO && !this.isImpersonating; }
  get isSSO(): boolean { return this.authService.isSSO(); }

  drawerOpenInitial = true;
  // for the FilePickerComponent (and potentially others) -- hide the toolbar and sidenav
  hideFraming: boolean = false;

  private readonly log = new LogHelper('Layout');
  private readonly destroy$ = new Subject<void>();

  get canOverrideFeaturesSupported(): boolean {
    return FeaturesSupportedService.canOverrideFeaturesSupported(this.authService);
  }

  ngOnInit() {
    AnalyticsHelper.addAttribute('appVersion', this.appVersionService.detectAppVersion());

    // apply cached theme, if present
    this.applyTheme(StorageHelper.getTheme());

    // Set the initial drawer state to open for desktop, closed for mobile
    this.drawerOpenInitial = this.viewState.isLargeIsh;

    // listen for layout changes
    this.layoutService.layoutInfo$.pipe(takeUntil(this.destroy$)).subscribe(_ =>
      this.changeDetectorRef.markForCheck());

    // Wire into events
    this.authService.user.data.pipe(takeUntil(this.destroy$)).subscribe(user =>
      this.onUserChanged(user));

    // Update page name when nav info changes
    this.layoutService.navInfo$.pipe(takeUntil(this.destroy$)).subscribe((info) => {
      if (info) {
        this.currentPage = info.name;
        AnalyticsHelper.updatePageTitle(this.translation.translate(this.currentPage ?? 'Unknown Page'));
      }
    });

    // see: https://stackoverflow.com/q/42981136/1363731
    this.router.events.pipe(takeUntil(this.destroy$)).subscribe((event) => {
      if (event.type === EventType.RouteConfigLoadStart) {
        this.loadingRouteConfig = true;
      } else if (event.type === EventType.RouteConfigLoadEnd) {
        this.loadingRouteConfig = false;
      } else if (event.type === EventType.NavigationEnd) {
        AnalyticsHelper.logPageView();
        this.hideFraming = event?.url?.includes(FilesViewConstants.RTE_ROUTE) === true;
        if (event?.url?.includes('navigation=collapsed') === true) {
          this.drawerOpenInitial = false;
        }
      }
    });

    // Check for Beta experience
    this.isBetaUx = StorageHelper.isBetaUx();
    if (!this.isBetaUx) {
      const rx$ = this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe((params) => {
        this.isBetaUx = !!params[NavService.betaQueryParam];
        if (this.isBetaUx) {
          // Save to cookie to survive auth redirects
          StorageHelper.saveIsBetaUx(true);
          // No longer need to listen to query
          rx$.unsubscribe();
        }
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.navDrawer) {
      this.navDrawer.openedChange.pipe(takeUntil(this.destroy$)).subscribe(() => {
        globalThis.dispatchEvent(new Event('resize'));
      });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get userDisplayName() {
    if (this.user) {
      if (this.user.name.givenName && this.user.name.familyName) {
        return this.user.name.givenName + ' ' + this.user.name.familyName;
      } else {
        return this.translation.translate('Auth.SignedIn');
      }
    } else {
      return null;
    }
  }

  get userTooltip(): string {
    let tip = this.userDisplayName;
    if (!tip) return '';
    if (this.user?.identity?.username) tip += `\nUsername: ${this.user.identity.username}`;
    if (this.user?.agency && this.user?.tenantId) tip += `\nTenant: ${this.user.agency} (ID: ${this.user.tenantId})`;
    return tip;
  }

  private onUserChanged(user: User) {
    this.user = user;
    this.applyTheme(this.theme);

    /**
     * display chatbot widget if enabled
     */
    if (this.user?.permissions?.includes(AccessControl.FeatureChatbotForethought)) {
      this.loadForethoughtChatbot();
    }

    if (this.user) {
      this.applyNavRules(user);

      // add analytics attributes for this user
      AnalyticsHelper.updateUser(user);

      // Get the user's theme
      this.settingsApi
        .getTheme()
        .pipe(takeUntil(this.destroy$))
        .subscribe((data) => {
          const theme = data?.item;
          if (theme) {
            StorageHelper.saveTheme(theme);
            this.applyTheme(theme);
          }

          // tell the layout service to start polling the banner config for our current brand/platform
          // note: if the banner is disabled via feature toggle, the service will handle that
          this.layoutService.startPollingBannerConfig(theme?.brandName);
        });
    }
  }

  private applyNavRules(user: User) {
    if (this.authService.isAdminUser(user)) {
      this.navService.setStandardNavigation();
    } else {
      this.isNonAdmin = true;
      this.navService.setNonAdminNavigation();
    }

    if (this.user.tenantId === 0) {
      this.navService.appendBackOfficeNavigation();
    }
  }

  applyTheme(theme?: Theme) {
    this.theme = theme;
    this.changeDetectorRef.markForCheck();
  }

  onRouteClick() {
    if (this.navDrawer.mode === 'over' && this.navDrawer.opened) {
      void this.navDrawer.close();
    }
  }

  onUserClick() {
    this.log.debug('onUserClick');
    // log detected app version to console
    this.appVersionService.fetchLatestVersion().subscribe((latest) => {
      const current = this.appVersionService.detectAppVersion();
      console.log(`App Version: current=${current}; latest=${latest}`);
    });

    AnalyticsHelper.logEvent({
      action: 'user-button-click',
      name: 'ButtonClick',
    });

    // useful for testing (add temporary code here as needed)
    // - this.authService.refreshAccessToken();
    // - this.authService.onSessionExpired();
    // - this.authService.onAuthEvent({ key: 'silentrenewerror', payload: 'login_required' });
  }

  onTitleLongPress() {
    this.log.debug('onTitleLongPress');
    void this.authService.refreshAccessToken();

    AnalyticsHelper.logEvent({
      action: 'nav-title-long-press',
      name: 'LongPress',
    });
  }

  showFeedback() {
    this.dialog.open(FeedbackComponent, { minWidth: '600px', data: { isBeta: this.isBetaExit }, autoFocus: false });
  }

  onViewAuthLog() {
    this.dialog.open(AuthEventsComponent, { minWidth: '600px' });
  }

  showChangeApi() {
    this.dialog.open(ApiSelectorComponent, { width: '800px', data: {} });
  }

  showEnableChangeEnvironments() {
    this.dialog
      .open(ConfirmComponent, {
        width: '400px',
        data: {
          title: 'Enable Change Environment',
          message: 'Do you want to enable changing environments (for testing against alternate hostnames)?',
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.apiService.enableChangeEnvironments();
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  showImpersonate() {
    this.dialog.open(ImpersonationComponent, { minWidth: '60vw', data: {} });
  }

  stopImpersonate() {
    this.authService.stopImpersonation();
  }

  leaveBeta() {
    this.isBetaExit = true;
    this.showFeedback();
  }

  isLoginPage() {
    return this.currentPage === 'Login';
  }

  logout() {
    this.user = null;
    this.authService.logout();
  }

  overrideFeatureSupported() {
    const dRef = this.dialog.open(SelectFromListDialogComponent, {
      ariaLabel: 'Override Supported Features',
      width: '600px',
      data: {
        title: 'Override Supported Features',
        items: FeaturesSupportedService.getFeatures().map((feature) => ({
          display: feature.feature,
          selected: feature.enabled,
          value: feature,
        })),
      },
      autoFocus: false,
    });

    dRef.afterClosed().subscribe((result: SelectFromListResult) => {
      if (result.result === 'select') {
        const features: string[] = result.selectedItems.map((o) => o.display);
        FeaturesSupportedService.overrideFeaturesSupported(this.authService, features);
        this.changeDetectorRef.markForCheck();
      }

      if (result.result === 'reset') {
        FeaturesSupportedService.resetFeaturesSupported(this.authService);
        this.changeDetectorRef.markForCheck();
      }

      if (result.result !== 'cancel') {
        const currentUrl = this.router.parseUrl(this.router.url);
        void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
          console.log('Reloading page to apply feature changes');
          void this.router.navigate(
            // take the full url without query params
            [currentUrl.toString().replace(/\?.*/, '')],
            // preserve query params and fragment
            { queryParams: currentUrl.queryParams, fragment: currentUrl.fragment },
          );
        });
      }
    });
  }

  changePassword() {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '80vw',
      maxWidth: '800px',
      autoFocus: true,
      delayFocusTrap: true,
    });
  }

  private loadForethoughtChatbot(): void {
    const scriptId = 'forethought-widget-embed-script';

    // Check if already loaded
    if (document.getElementById(scriptId)) {
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = 'https://solve-widget.forethought.ai/embed.js';
    script.id = scriptId;

    // Set all the required attributes
    script.setAttribute('initial-intent-id', '');
    script.setAttribute('config-ft-placement', 'bottom_right');
    script.dataset.apiKey = 'c9c31f19-2cc5-4bd1-a1c4-64de706ada6c';
    script.dataset.ftEmbedScriptLanguage = 'English';
    script.dataset.ftWorkflowTag = 'Mass Notification';
    script.dataset.ftEmail = '';

    script.onerror = (err) => {
      this.log.debug('Forethought chatbot: failed to load', err);
    };

    script.onload = () => {
      this.log.debug('Forethought chatbot: loaded');
    };

    document.body.appendChild(script);
  }
}
